import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Sparkles, Save, RotateCcw, Eye, ShieldCheck, ShieldAlert, Cpu } from "lucide-react";
import { API } from "../App";

const TYPES = [
  { key: "initial", label: "Entretien initial" },
  { key: "suivi_tcc", label: "Séance de suivi TCC" },
];

const ModelSelector = ({ data, onChange }) => {
  const route = data.routes?.[data.model] || data.route;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="report-model-block">
      <Label className="flex items-center gap-2"><Cpu className="w-4 h-4" /> Modèle IA pour les comptes rendus</Label>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-center">
        <select
          value={data.model || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 text-sm border rounded-lg bg-background border-border"
          data-testid="report-model-select"
        >
          {(data.models || []).map((m) => (
            <option key={m.id} value={m.id}>{m.label}{data.routes?.[m.id] ? ` — ${data.routes[m.id].label}` : ""}</option>
          ))}
        </select>
        {route && (
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${route.hds ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"}`} data-testid="report-route-badge">
            {route.hds ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
            {route.hds ? "Vos clés / Vertex AI (HDS)" : "Clé de la plateforme"}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Même ordre de priorité que les e-mails de suivi : vos clés personnelles, puis Vertex AI (UE), puis la clé de la plateforme.{data.model_is_default && " Modèle choisi automatiquement selon vos clés."}</p>
    </div>
  );
};

const EXAMPLES = [
  "Rédige en paragraphes courts plutôt qu'en listes à puces.",
  "Vouvoie toujours le patient et désigne-le par « le patient » / « la patiente ».",
  "Privilégie le vocabulaire de la 3e vague (ACT, pleine conscience) quand c'est pertinent.",
  "Termine chaque champ « Plan » par une phrase d'objectif mesurable.",
];

const ReportPromptsCard = () => {
  const [data, setData] = useState({ initial: "", suivi_tcc: "", model: "", defaults: {}, models: [], routes: {}, max_length: 4000 });
  const [saved, setSaved] = useState({ initial: "", suivi_tcc: "", model: "" });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/report-prompts`, { withCredentials: true }).then((r) => {
      setData(r.data);
      setSaved({ initial: r.data.initial, suivi_tcc: r.data.suivi_tcc, model: r.data.model });
    }).catch(() => {});
  }, []);

  const dirty = data.initial !== saved.initial || data.suivi_tcc !== saved.suivi_tcc || data.model !== saved.model;

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/report-prompts`, { initial: data.initial, suivi_tcc: data.suivi_tcc, model: data.model }, { withCredentials: true });
      setSaved({ initial: r.data.initial, suivi_tcc: r.data.suivi_tcc, model: r.data.model });
      setData(r.data);
      toast.success("Consignes et modèle enregistrés");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const reset = (key) => setData((d) => ({ ...d, [key]: "" }));

  return (
    <Card data-testid="report-prompts-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Sparkles className="w-5 h-5" /> Prompts personnalisés des comptes rendus</CardTitle>
        <p className="text-sm text-muted-foreground">Vos consignes de style sont ajoutées au prompt de l'IA lors de la génération d'un compte rendu (depuis une transcription, un audio ou un document). La structure des champs reste inchangée.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <ModelSelector data={data} onChange={(model) => setData((d) => ({ ...d, model, model_is_default: false }))} />
        {TYPES.map((t) => (
          <div key={t.key} className="space-y-1.5" data-testid={`prompt-block-${t.key}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label>{t.label}</Label>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setPreview(preview === t.key ? null : t.key)} data-testid={`prompt-preview-${t.key}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> {preview === t.key ? "Masquer" : "Voir"} le prompt complet
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" disabled={!data[t.key]} onClick={() => reset(t.key)} data-testid={`prompt-reset-${t.key}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Effacer
                </Button>
              </div>
            </div>
            <textarea
              value={data[t.key] || ""}
              onChange={(e) => setData((d) => ({ ...d, [t.key]: e.target.value }))}
              rows={4}
              maxLength={data.max_length}
              placeholder={`Ex. : ${EXAMPLES[t.key === "initial" ? 0 : 1]}`}
              className="w-full px-3 py-2 text-sm border rounded-lg bg-background border-border"
              data-testid={`prompt-textarea-${t.key}`}
            />
            <p className="text-xs text-muted-foreground text-right">{(data[t.key] || "").length} / {data.max_length}</p>
            {preview === t.key && (
              <pre className="text-[11px] font-mono whitespace-pre-wrap bg-muted/40 border rounded-lg p-3 max-h-72 overflow-y-auto" data-testid={`prompt-preview-text-${t.key}`}>
                {data.defaults?.[t.key]}
                {data[t.key]?.trim() && `\n\nCONSIGNES PERSONNELLES DU THÉRAPEUTE :\n${data[t.key].trim()}`}
              </pre>
            )}
          </div>
        ))}
        <div className="rounded-lg bg-muted/30 border border-dashed p-3">
          <p className="text-xs font-medium mb-1">Idées de consignes</p>
          <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
            {EXAMPLES.map((e) => <li key={e}>{e}</li>)}
          </ul>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || !dirty} className="gap-2" data-testid="save-report-prompts-btn">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer les consignes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportPromptsCard;
